import { prefersReducedMotion } from '@theme/utilities';

/**
 * Generic reveal-on-scroll: observes matching descendants and adds a
 * "visible" class the first time each one scrolls into view. Pair with CSS
 * driven by the same two classes for the hidden -> visible transition.
 *
 * The hidden state is applied by this element (via `initial-class`) rather
 * than baked into CSS, so a page with JS disabled/blocked just shows
 * everything immediately instead of stuck invisible.
 *
 * @attr {string} [selector] - CSS selector (relative to this element) for
 *   the items to reveal. Defaults to this element's direct children.
 * @attr {string} [initial-class] - Class added to each item up front,
 *   before it has scrolled into view. Defaults to "scroll-reveal--hidden".
 * @attr {string} [reveal-class] - Class added to each item once visible.
 *   Defaults to "scroll-reveal--visible".
 * @attr {string} [threshold] - IntersectionObserver threshold (0-1).
 * @attr {string} [root-margin] - IntersectionObserver rootMargin.
 *
 * @example
 * <scroll-reveal selector=".card" initial-class="card--reveal" reveal-class="card--visible">
 *   ...
 * </scroll-reveal>
 */
class ScrollReveal extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    if (prefersReducedMotion()) return;

    const selector = this.getAttribute('selector');
    const items = selector ? Array.from(this.querySelectorAll(selector)) : Array.from(this.children);
    if (!items.length) return;

    const initialClass = this.getAttribute('initial-class') || 'scroll-reveal--hidden';
    const revealClass = this.getAttribute('reveal-class') || 'scroll-reveal--visible';
    const threshold = Number(this.getAttribute('threshold')) || 0.2;
    const rootMargin = this.getAttribute('root-margin') || '0px 0px -10% 0px';

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add(revealClass);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin }
    );

    for (const item of items) {
      item.classList.add(initialClass);
      this.#observer.observe(item);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('scroll-reveal')) {
  customElements.define('scroll-reveal', ScrollReveal);
}
